<template>
  <el-dialog
    title="关联新房屋"
    :visible.sync="visible"
    width="1000px"
    :before-close="handleClose"
    :append-to-body="true"
  >
    <!-- 复用可拖拽布局（同房屋列表） -->
    <div class="resizable-layout">
      <!-- 左侧楼栋单元树 -->
      <div class="tree-panel" :style="{ width: treeWidth + 'px' }">
        <floor-unit-tree
          ref="floorUnitTree"
          @switchFloorUnit="switchFloorUnit"
        />
      </div>

      <!-- 拖拽分隔线 -->
      <div
        class="resize-handle"
        @mousedown="startResize"
        @mouseover="showResizeCursor"
        @mouseleave="hideResizeCursor"
      >
        <div class="resize-line"></div>
      </div>

      <!-- 右侧房屋列表（带多选框） -->
      <div
        class="content-panel"
        :style="{ width: 'calc(100% - ' + (treeWidth + 10) + 'px)' }"
      >
        <el-card>
          <!-- 带多选框的房屋表格 -->
          <el-table
            ref="roomTableRef"
            :data="roomList"
            border
            stripe
            style="width: 100%"
            @selection-change="handleSelectionChange"
            :loading="loading || loadingRelatedRooms"
          >
            :empty-text="loading ? '加载中...' : '暂无匹配房屋数据'" >
            <el-table-column type="selection" width="55" />

            <el-table-column label="房间号" width="120" align="center">
              <template slot-scope="scope">
                {{ scope.row.floorNum }}-{{ scope.row.unitNum }}-{{
                  scope.row.roomNum
                }}
              </template>
            </el-table-column>
            <el-table-column label="地址" align="center" min-width="180">
              <template slot-scope="scope">
                {{ scope.row.address || "-" }}
              </template>
            </el-table-column>
            <el-table-column label="面积(㎡)" align="center" width="120">
              <template slot-scope="scope">
                {{
                  (scope.row.builtUpArea || 0) + "/" + (scope.row.roomArea || 0)
                }}
              </template>
            </el-table-column>
            <!-- 采暖类型显示层高列 -->
            <el-table-column
              v-if="feeType === 'heating'"
              label="层高(m)"
              align="center"
              width="120"
            >
              <template slot-scope="scope">
                {{ Number(scope.row.floorHeight || 0).toFixed(2) }}
              </template>
            </el-table-column>
            <el-table-column
              label="房屋类型"
              prop="roomSubTypeName"
              width="120"
              align="center"
            />
            <el-table-column label="状态" width="120" align="center">
              <template slot-scope="scope">
                <el-tag :type="getStatusTagType(scope.row.state)">
                  {{ scope.row.stateName || "未知" }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="关联状态" width="120" align="center">
              <template slot-scope="scope">
                <el-tag
                  type="success"
                  v-if="relatedRoomIds.includes(scope.row.roomId)"
                >
                  已关联
                </el-tag>
                <el-tag type="default" v-else> 未关联 </el-tag>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页 -->
          <el-pagination
            class="mt-20"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 30]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            v-if="total > 0"
          />
        </el-card>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        @click="handleConfirm"
        :disabled="selectedRooms.length === 0"
      >
        确认关联（已选{{ selectedRooms.length }}个）
      </el-button>
    </div>

    <!-- 新增/编辑房屋弹窗 -->
    <el-dialog
      title="房屋信息"
      :visible.sync="roomDialogVisible"
      width="600px"
      :before-close="closeRoomDialog"
    >
      <el-form
        ref="roomForm"
        :model="roomForm"
        :rules="roomRules"
        label-width="120px"
      >
        <el-form-item label="楼栋号" prop="floorNum">
          <el-input v-model="roomForm.floorNum" placeholder="请输入楼栋号" />
        </el-form-item>
        <el-form-item label="单元号" prop="unitNum">
          <el-input v-model="roomForm.unitNum" placeholder="请输入单元号" />
        </el-form-item>
        <el-form-item label="房间号" prop="roomNum">
          <el-input v-model="roomForm.roomNum" placeholder="请输入房间号" />
        </el-form-item>
        <el-form-item label="详细地址" prop="address">
          <el-input v-model="roomForm.address" placeholder="请输入详细地址" />
        </el-form-item>
        <el-form-item label="建筑面积(㎡)" prop="builtUpArea">
          <el-input
            v-model="roomForm.builtUpArea"
            type="number"
            placeholder="请输入建筑面积"
          />
        </el-form-item>
        <el-form-item label="房间面积(㎡)" prop="roomArea">
          <el-input
            v-model="roomForm.roomArea"
            type="number"
            placeholder="请输入房间面积"
          />
        </el-form-item>
        <!-- 采暖类型新增层高字段 -->
        <el-form-item
          v-if="feeType === 'heating'"
          label="层高(m)"
          prop="floorHeight"
        >
          <el-input
            v-model="roomForm.floorHeight"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="请输入房屋层高"
          />
        </el-form-item>
        <el-form-item label="房屋类型" prop="roomSubType">
          <el-select
            v-model="roomForm.roomSubType"
            placeholder="请选择房屋类型"
          >
            <el-option
              v-for="(item, index) in roomTypes"
              :key="index"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="房屋状态" prop="state">
          <el-select v-model="roomForm.state" placeholder="请选择房屋状态">
            <el-option
              v-for="(item, index) in states"
              :key="index"
              :label="item.name"
              :value="item.statusCd"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="closeRoomDialog">取消</el-button>
        <el-button type="primary" @click="saveRoom">保存</el-button>
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { addRoom } from "@/api/room/roomApi";
import {
  relateRuleToRooms,
  getHouseListByRuleId,
  queryRuleRelRooms,
} from "@/api/community/calcRuleApi"; // 新增：查询已关联房屋ID接口
import FloorUnitTree from "@/components/room/floorUnitTree";
import { getCommunityId } from "@/api/community/communityApi";

export default {
  name: "RelateNewHouseDialog",
  components: { FloorUnitTree },
  props: {
    // 接收关联的规则ID
    ruleId: {
      type: String,
      required: true,
    },
    // 接收每平米价格（必传，用于计算calcValue）
    pricePerSqm: {
      type: [Number, String], // 兼容数字和字符串类型
      required: true,
      validator: (value) => {
        // 校验：必须是大于0的有效数字
        const num = Number(value);
        return !isNaN(num) && num > 0;
      },
    },
    // 新增：接收费用类型（rent/heating）
    feeType: {
      type: String,
      required: true,
      validator: (value) => {
        return ["rent", "heating"].includes(value);
      },
    },
  },
  data() {
    return {
      visible: false,
      loading: false,
      loadingRelatedRooms: false, // 新增：查询已关联房屋加载态
      // 搜索条件：新增feeType
      searchForm: {
        floorId: "",
        unitId: "",
        roomId: "",
        state: "",
        roomSubType: "",
        communityId: "", // 初始为空，后续异步赋值
        feeType: "", // 新增：费用类型参数
      },
      // 房屋列表数据
      roomList: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      // 房屋状态字典（补充完整）
      states: [
        { statusCd: "2002", name: "未入住" },
        { statusCd: "2003", name: "已入住" },
        { statusCd: "2004", name: "已退租" },
        { statusCd: "2005", name: "锁定" },
      ],
      // 房屋类型字典（补充完整）
      roomTypes: [
        { code: "10", name: "住宅" },
        { code: "20", name: "商业" },
        { code: "30", name: "工业" },
        { code: "40", name: "商铺" },
        { code: "50", name: "其他" },
      ],
      // 选中的房屋
      selectedRooms: [],
      // 已关联的房屋ID列表（核心：用于自动勾选和状态显示）
      relatedRoomIds: [],
      // 拖拽相关
      treeWidth: 300,
      minTreeWidth: 200,
      maxTreeWidth: 600,
      isResizing: false,
      startX: 0,
      startWidth: 0,
      // 新增/编辑房屋相关
      roomDialogVisible: false,
      roomForm: {
        roomId: "",
        floorNum: "",
        unitNum: "",
        roomNum: "",
        address: "",
        builtUpArea: "",
        roomArea: "",
        floorHeight: "", // 新增：层高字段
        roomSubType: "",
        state: "",
        communityId: "",
      },
      roomRules: {
        floorNum: [
          { required: true, message: "请输入楼栋号", trigger: "blur" },
        ],
        unitNum: [{ required: true, message: "请输入单元号", trigger: "blur" }],
        roomNum: [{ required: true, message: "请输入房间号", trigger: "blur" }],
        builtUpArea: [
          { required: true, message: "请输入建筑面积", trigger: "blur" },
          {
            type: "number",
            min: 0.01,
            message: "建筑面积必须大于0",
            trigger: "blur",
          },
        ],
        roomArea: [
          { required: true, message: "请输入房间面积", trigger: "blur" },
          {
            type: "number",
            min: 0.01,
            message: "房间面积必须大于0",
            trigger: "blur",
          },
        ],
        floorHeight: [
          // 层高校验（仅采暖类型生效）
          { required: true, message: "请输入层高", trigger: "blur" },
          {
            type: "number",
            min: 0.01,
            message: "层高必须大于0",
            trigger: "blur",
          },
        ],
        roomSubType: [
          { required: true, message: "请选择房屋类型", trigger: "change" },
        ],
        state: [
          { required: true, message: "请选择房屋状态", trigger: "change" },
        ],
      },
    };
  },
  watch: {
    // 新增：规则ID变化时，重新查询已关联房屋
    ruleId(newVal) {
      if (newVal && this.visible) {
        this.fetchRelatedRoomIds();
      }
    },
    // 新增：费用类型变化时，刷新列表（重新查询带feeType）
    feeType(newVal) {
      if (newVal && this.visible) {
        this.searchForm.feeType = newVal;
        this.queryRooms();
      }
    },
    // 新增：房屋列表变化时，自动勾选已关联房屋
    roomList() {
      this.autoCheckRelatedRooms();
    },
  },
  async mounted() {
    // 1. 优先异步获取社区ID（解决null问题）
    await this.getCommunityIdAsync();
    // 2. 初始化费用类型到搜索条件
    this.searchForm.feeType = this.feeType;
    // 3. 初始化字典数据
    this.queryRooms();
    // 4. 监听拖拽事件
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mouseup", this.onMouseUp);
  },
  beforeDestroy() {
    // 移除全局拖拽事件监听
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
  },
  methods: {
    /**
     * 异步获取社区ID
     */
    async getCommunityIdAsync() {
      try {
        const communityId = await getCommunityId();
        if (communityId) {
          this.searchForm.communityId = communityId;
          this.roomForm.communityId = communityId;
          console.log("获取社区ID成功:", communityId);
        } else {
          throw new Error("社区ID为空");
        }
      } catch (error) {
        this.$message.error("获取社区信息失败，请刷新页面重试");
        console.error("获取社区ID异常:", error);
      }
    },

    /**
     * 打开弹窗（支持参数覆盖，增强灵活性）
     */
    open(params = {}) {
      // 覆盖参数（如果传入）
      if (params.ruleId) this.ruleId = params.ruleId;
      if (params.pricePerSqm) this.pricePerSqm = params.pricePerSqm;
      if (params.feeType) this.feeType = params.feeType;

      // 校验必要参数
      if (!this.ruleId) {
        this.$message.error("规则ID不能为空");
        return;
      }
      if (
        !this.pricePerSqm ||
        isNaN(Number(this.pricePerSqm)) ||
        Number(this.pricePerSqm) <= 0
      ) {
        this.$message.error("每平米价格必须是大于0的有效数字");
        return;
      }
      if (!this.feeType || !["rent", "heating"].includes(this.feeType)) {
        this.$message.error("费用类型必须为房租(rent)或采暖(heating)");
        return;
      }

      // 初始化费用类型到搜索条件
      this.searchForm.feeType = this.feeType;
      this.visible = true;
      this.resetSearch(); // 打开时重置搜索条件（保留社区ID和feeType）
    },

    /**
     * 新增：查询当前规则已关联的房屋ID列表（携带feeType）
     */
    async fetchRelatedRoomIds() {
      if (!this.ruleId || !this.feeType) return;

      this.loadingRelatedRooms = true;
      try {
        const res = await getHouseListByRuleId({
          ruleId: this.ruleId,
          feeType: this.feeType, // 新增：携带费用类型
        });
        if (res.code === 0) {
          this.relatedRoomIds = res.data || []; // 接口返回格式：{ code:0, data: ["roomId1", "roomId2"] }
        } else {
          this.$message.warning("获取已关联房屋信息失败");
          this.relatedRoomIds = [];
        }
        console.log(this.relatedRoomIds, "relatedRoomIds");
      } catch (error) {
        console.error("查询已关联房屋异常:", error);
        this.$message.error("查询已关联房屋失败");
        this.relatedRoomIds = [];
      } finally {
        this.loadingRelatedRooms = false;
        this.autoCheckRelatedRooms(); // 数据返回后自动勾选
      }
    },

    /**
     * 关闭弹窗
     */
    handleClose() {
      this.visible = false;
      this.selectedRooms = []; // 关闭时清空选中状态
      this.relatedRoomIds = []; // 清空已关联房屋ID缓存
      this.$emit("close");
    },

    /**
     * 切换楼栋单元（树形组件回调）
     */
    switchFloorUnit(data) {
      this.searchForm.floorId = data.floorId || "";
      this.searchForm.unitId = data.unitId || "";
      this.currentPage = 1; // 切换条件时重置为第一页
      this.queryRooms();
    },

    /**
     * 查询房屋列表（携带feeType参数）
     */
    async queryRooms(showAll = false) {
      // 校验社区ID和费用类型是否存在
      if (!this.searchForm.communityId) {
        this.$message.warning("社区信息未获取，请稍后重试");
        return;
      }
      if (!this.searchForm.feeType) {
        this.$message.warning("费用类型未设置，请刷新页面重试");
        return;
      }

      this.loading = true;
      try {
        const params = {
          ...(showAll
            ? {
                communityId: this.searchForm.communityId,
                feeType: this.searchForm.feeType, // 新增：携带费用类型
              }
            : this.searchForm),
          page: this.currentPage,
          row: this.pageSize,
        };
        const res = await queryRuleRelRooms(params);
        this.roomList = res.rooms || [];
        this.total = res.total || 0;
      } catch (error) {
        this.$message.error("获取房屋列表失败");
        console.error("查询房屋异常:", error);
        this.roomList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 重置搜索条件（保留社区ID和feeType）
     */
    resetSearch() {
      this.searchForm = {
        floorId: "",
        unitId: "",
        roomId: "",
        state: "",
        roomSubType: "",
        communityId: this.searchForm.communityId, // 保留社区ID
        feeType: this.searchForm.feeType, // 保留费用类型
      };
      this.currentPage = 1;
      this.queryRooms();
    },

    /**
     * 分页-每页条数改变
     */
    handleSizeChange(val) {
      this.pageSize = val;
      this.currentPage = 1;
      this.queryRooms();
    },

    /**
     * 分页-当前页改变
     */
    handleCurrentChange(val) {
      this.currentPage = val;
      this.queryRooms();
    },

    /**
     * 表格多选事件
     */
    handleSelectionChange(val) {
      this.selectedRooms = val;
    },

    /**
     * 保存新增房屋（采暖类型携带层高）
     */
    async saveRoom() {
      try {
        // 1. 表单校验（仅校验当前需要的字段）
        const validateFields =
          this.feeType === "heating"
            ? [
                "floorNum",
                "unitNum",
                "roomNum",
                "builtUpArea",
                "roomArea",
                "floorHeight",
                "roomSubType",
                "state",
              ]
            : [
                "floorNum",
                "unitNum",
                "roomNum",
                "builtUpArea",
                "roomArea",
                "roomSubType",
                "state",
              ];

        await this.$refs.roomForm.validateField(validateFields);

        // 2. 数据预处理（确保面积/层高为数字类型）
        const roomData = {
          ...this.roomForm,
          builtUpArea: Number(this.roomForm.builtUpArea),
          roomArea: Number(this.roomForm.roomArea),
          floorHeight:
            this.feeType === "heating" ? Number(this.roomForm.floorHeight) : 0, // 采暖类型传递层高
          feeType: this.feeType, // 新增：携带费用类型
        };

        // 3. 调用新增房屋接口
        const res = await addRoom(roomData);
        if (res.code === 0) {
          this.$message.success("房屋新增成功");
          this.closeRoomDialog();
          this.queryRooms(); // 刷新房屋列表，显示新增的房屋
        } else {
          this.$message.error(res.msg || "房屋新增失败");
        }
      } catch (error) {
        console.error("新增房屋异常:", error);
        // 区分表单校验错误和接口错误
        if (error.name !== "Error") {
          return; // 表单校验错误，无需额外提示
        }
        this.$message.error("房屋新增失败，请重试");
      }
    },

    /**
     * 确认关联房屋（携带feeType，适配采暖计算逻辑）
     */
    async handleConfirm() {
      const roomIds = this.selectedRooms.map((room) => room.roomId);
      if (roomIds.length === 0) {
        this.$message.warning("请选择要关联的房屋");
        return;
      }

      try {
        // 1. 转换每平米价格为数字（避免字符串类型导致计算异常）
        const pricePerSqm = Number(this.pricePerSqm);

        // 2. 构建请求参数（携带feeType，适配不同计算逻辑）
        const requestData = {
          ruleId: this.ruleId, // 父组件传递的规则ID
          feeType: this.feeType, // 新增：携带费用类型
          roomRelations: this.selectedRooms.map((room) => {
            // 房间面积：默认0，避免undefined/非数字导致NaN
            const builtUpArea = Number(room.builtUpArea) || 0;
            // 计算价值：根据费用类型区分逻辑
            let calcValue = 0;
            if (this.feeType === "rent") {
              // 房租：单价 × 面积
              calcValue = (builtUpArea * pricePerSqm).toFixed(2);
            } else if (this.feeType === "heating") {
              // 采暖：单价 × 面积 × 层高
              const floorHeight = Number(room.floorHeight) || 0;
              calcValue = (builtUpArea * pricePerSqm * floorHeight).toFixed(2);
            }
            return {
              roomId: room.roomId,
              calcValue: calcValue, // 最终为字符串类型，如"123456.78"
              feeType: this.feeType, // 每条关联记录携带费用类型
            };
          }),
        };

        // 3. 调用关联接口
        const res = await relateRuleToRooms(requestData);
        if (res.code === 0) {
          this.$message.success(`成功关联${roomIds.length}个房屋`);
          this.relatedRoomIds = roomIds; // 更新已关联房屋ID列表
          this.handleClose();
          this.$emit("relateSuccess"); // 通知父组件刷新关联列表
        } else {
          this.$message.error(res.msg || "房屋关联失败");
        }
      } catch (error) {
        console.error("房屋关联异常:", error);
        this.$message.error("房屋关联失败，请重试");
      }
    },

    /**
     * 拖拽相关方法
     */
    startResize(event) {
      event.preventDefault();
      this.isResizing = true;
      this.startX = event.clientX;
      this.startWidth = this.treeWidth;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },

    onMouseMove(event) {
      if (!this.isResizing) return;
      const deltaX = event.clientX - this.startX;
      const newWidth = this.startWidth + deltaX;
      // 限制宽度范围
      if (newWidth >= this.minTreeWidth && newWidth <= this.maxTreeWidth) {
        this.treeWidth = newWidth;
      }
    },

    onMouseUp() {
      if (this.isResizing) {
        this.isResizing = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    },

    showResizeCursor() {
      if (!this.isResizing) {
        document.body.style.cursor = "col-resize";
      }
    },

    hideResizeCursor() {
      if (!this.isResizing) {
        document.body.style.cursor = "";
      }
    },

    /**
     * 房屋状态标签样式
     */
    getStatusTagType(state) {
      switch (state) {
        case "2002": // 未入住
          return "info";
        case "2003": // 已入住
          return "success";
        case "2004": // 已退租
          return "warning";
        case "2005": // 锁定
          return "danger";
        default:
          return "default";
      }
    },

    /**
     * 关闭房屋表单弹窗
     */
    closeRoomDialog() {
      this.roomDialogVisible = false;
      this.$refs.roomForm.resetFields();
    },

    /**
     * 自动勾选已关联房屋（补充实现）
     */
    autoCheckRelatedRooms() {
      if (!this.$refs.roomTableRef || !this.relatedRoomIds.length) return;
      this.roomList.forEach((room) => {
        if (this.relatedRoomIds.includes(room.roomId)) {
          this.$refs.roomTableRef.toggleRowSelection(room, true);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.resizable-layout {
  display: flex;
  height: 500px;
  position: relative;
}

.tree-panel {
  background: #fff;
  border-radius: 4px;
  padding: 10px;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  transition: width 0.2s ease;
}

.content-panel {
  overflow-y: auto;
  padding-left: 10px;
}

.resize-handle {
  width: 10px;
  cursor: col-resize;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle:hover .resize-line {
  background-color: #409eff;
}

.resize-line {
  width: 2px;
  height: 30px;
  background-color: #dcdfe6;
  border-radius: 1px;
  transition: background-color 0.2s;
}

.mb-20 {
  margin-bottom: 20px;
}

.mt-20 {
  margin-top: 20px;
  text-align: right;
}

.button-group {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
}

/* 表格空数据样式优化 */
.el-table__empty-text {
  color: #606266;
  font-size: 14px;
  padding: 60px 0;
}

/* 标签样式优化 */
.el-tag {
  font-size: 12px;
  padding: 2px 8px;
}
</style>
